import * as Ariakit from '@ariakit/react';
import { css, styled } from 'styled-components';
import { ReactNode } from 'react';
import * as React from 'react';

import { Text } from '../Text/Text';
import { theme } from '../../styles';
import { useTabbing } from '../../utils';

type TooltipSize = 'standard' | 'small';

type StyledTooltipProps = {
  size: TooltipSize;
};

type TooltipProps = {
  /** id to open the Tooltip, and to add a data-testid */
  id: string;
  /** The title/label that is meant to show on the content of the tooltip */
  contentLabel?: string;
  /** Descriptive text to be shown inside the Tooltip */
  content?: string | React.ReactNode;
  /** If set to `text` then the Tooltip "placeholder" will have a bottom border, otherwise it won't   */
  variant?: 'text' | 'object';
  linkUrl?: string;
  linkText?: string;
  /** Set true true to toggle open on hover instead of click */
  openOnHover?: boolean;
  /** Controls fontsize and padding */
  size?: TooltipSize;
  /** Stops an event from being passed on to other elements */
  stopPropagation?: boolean;
  /** Insert text or object as a placeholder  */
  children: ReactNode;
  /** Show tooltip only when conditionalTooltip is true. */
  conditionalTooltip?: boolean;
};

export const TooltipAriakit = ({
  id,
  variant = 'text',
  contentLabel,
  content,
  // isInTable = false,
  linkUrl,
  linkText,
  size = 'standard',
  children,
  conditionalTooltip = false,
}: TooltipProps) => {
  const isTabbing = useTabbing();

  return conditionalTooltip ? (
    <TooltipProvider
      variant={variant}
      data-tooltip-id={id}
      data-testid={`test-${id}`}
      isTabbing={isTabbing}
      placement="top-start"
    >
      <TooltipAnchor>{children}</TooltipAnchor>
      <Tooltip className="tooltip" size={size}>
        <Ariakit.TooltipArrow />
        {contentLabel && (
          <Text
            size={size}
            color="black90"
            fontWeight="semibold"
            renderAs="span"
            marginBottom={2}
            style={{ display: 'block' }}
            textAlign="left"
          >
            {contentLabel}
          </Text>
        )}
        {(content && typeof content === 'string') || linkUrl ? (
          <Text
            size={size}
            color="black90"
            renderAs="span"
            style={{ display: 'block' }}
            whiteSpace="pre-line"
            textAlign="left"
          >
            {content}
            {linkUrl && (
              <>
                {' '}
                <StyledLink target="_blank" href={linkUrl}>
                  {linkText}
                </StyledLink>
              </>
            )}
          </Text>
        ) : (
          content
        )}
      </Tooltip>
    </TooltipProvider>
  ) : (
    <>{children}</>
  );
};

const TooltipProvider = styled(Ariakit.TooltipProvider)<{ isTabbing: boolean; variant: string }>`
  background: none;
  border: none;
  padding: 0;
  appearance: none;
  outline: none;
  font-size: inherit;
  line-height: inherit;
  font-weight: inherit;
  font-family: inherit;
  color: inherit;
  display: inline-flex;
  cursor: pointer;

  ${props =>
    props.isTabbing &&
    css`
      &:focus {
        outline: ${theme.colors.darkBlue} solid 2px;
        outline-offset: 2px;
      }
    `}

  ${props =>
    props.variant === 'text' &&
    css`
      background-position: 1px bottom;
      background-repeat: repeat-x;
      background-size: 6px 1px;
      background-image: linear-gradient(to right, ${theme.colors.black90} 50%, transparent 0%);
      line-height: 1;
    `}
`;

const Tooltip = styled(Ariakit.Tooltip)<StyledTooltipProps>`
  background-color: ${theme.colors.blueHover} !important;
  opacity: 1 !important;
  border-radius: 2px;
  box-sizing: border-box;
  width: ${props => (props.size === 'small' ? 'min(300px, 100%)' : 'min(335px, 100%)')}!important;
  padding: ${props => (props.size === 'small' ? '0.625rem 0.75rem ' : '1.25rem 1.25rem 1.875rem 1.25rem')} !important;
  z-index: 1;
`;

const TooltipAnchor = styled(Ariakit.TooltipAnchor)``;

const StyledLink = styled.a`
  color: black;
`;
